import { ActivatedRoute, Router } from '@angular/router'
import { Component, OnInit } from '@angular/core'
import { WebsiteTemplatesService } from '../website-templates.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { MatDialog } from '@angular/material/dialog'
import { WorksDialogComponent } from 'src/app/pages/works-dialog/works-dialog.component'
import { PagesService } from '../pages.service'
import { getHeaderTemplateStyle } from 'src/app/blocks-wrapper/block-header/header-template-styles'

@Component({
  selector: 'app-website-templates-new',
  templateUrl: './website-templates-new.component.html',
  styleUrls: ['./website-templates-new.component.scss']
})
export class WebsiteTemplatesNewComponent implements OnInit {
  templatesList: Array<any> = []
  showTemplatesProgress = false

  applyDialogOpen = false
  applyDialogTemplate: any = null

  constructor(
    private templateService: WebsiteTemplatesService,
    private router: Router,
    private route: ActivatedRoute,
    public builderService: BuilderService,
    public dialog: MatDialog,
    private pagesService: PagesService
  ) {
    this.showTemplatesProgress = true
    this.loadTemplates()
  }

  ngOnInit(): void {}

  /**
   * Load all website templates from Supabase
   */
  loadTemplates = () => {
    this.templatesList = []
    this.templateService.getTemplates().subscribe(
      (templates: any) => {
        if (templates) {
          for (const key in templates) {
            if (Object.prototype.hasOwnProperty.call(templates, key)) {
              const element = templates[key]
              this.templatesList.push({
                img: element.template_image,
                name: element.template_name,
                pages: element.pages,
                statics: element.statics,
                template_id: key,
                template_category: element.category
              })
            }
          }
        }
        this.showTemplatesProgress = false
        if (window.screen.width > 768) {
          document.querySelector('template-view')?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
        }
      },
      () => { this.showTemplatesProgress = false }
    )
  }

  selectNewTemplate = (template: any) => {
    this.applyDialogTemplate = template
    this.applyDialogOpen = true
  }

  onApplyDialogConfirmed = () => {
    this.applyDialogOpen = false
    const template = this.applyDialogTemplate
    const projectId = this.builderService.selectedProject
    const pages = template.pages || {}
    const statics = template.statics || {}
    const completeHeaderData = this.getCompleteHeaderData(statics.header || {})
    const footerData = statics.footer || {}

    this.builderService.showUpdateProgress = true

    this.pagesService.submitWebsitePages(projectId, pages).subscribe(
      () => {
        this.pagesService.setStaticBlock(projectId, 'header', completeHeaderData).subscribe(
          () => {
            this.pagesService.setStaticBlock(projectId, 'footer', footerData).subscribe(
              () => {
                this.builderService.showUpdateProgress = false
                this.router.navigate(['/website-builder'], { relativeTo: this.route }).then(() => {
                  window.location.reload()
                })
              },
              () => { this.builderService.showUpdateProgress = false }
            )
          },
          () => { this.builderService.showUpdateProgress = false }
        )
      },
      () => { this.builderService.showUpdateProgress = false }
    )
  }

  onApplyDialogClosed = () => {
    this.applyDialogOpen = false
    this.applyDialogTemplate = null
  }

  /**
   * Get template-specific style for each header template.
   * Each of the 6 templates has its own unique design and colors.
   * Now uses centralized shared configuration from header-template-styles.ts
   */
  getTemplateStyle(templateId: string): any {
    return getHeaderTemplateStyle(templateId)
  }

  /**
   * Get complete header data structure with template-specific styles.
   *
   * WHY THIS IS NEEDED:
   * Each template (template1-6) has its own unique design, colors, and fonts.
   * This function ensures the correct style is saved for the selected template.
   *
   * WHAT THIS DOES:
   * 1. Takes the template data from Firebase
   * 2. Looks at which template is selected (template1-6)
   * 3. Gets that template's specific style configuration
   * 4. Merges everything together and returns complete object
   *
   * EXAMPLE:
   * Template has: { data: { headerTemplate: 'template1' } }
   * This function returns: Complete object with template1's unique colors, fonts, icons
   */
  getCompleteHeaderData = (templateHeaderData: any): any => {
    const defaultHeader = {
      id: 'static-header',
      type: 'header',
      data: {
        headerTemplate: 'template2',
        logoUrl: '',
        storeName: 'Store',
        navItems: [
          { label: 'Home', page: 'home', index: 0 },
          { label: 'Products', page: 'products', index: 1 },
          { label: 'About', page: 'about', index: 2 }
        ],
        isAuthenticated: false,
        showLanguageSwitcher: true,
        showCart: true,
        showSearch: false,
        showWishlist: false,
        type: 'color',
        bgColor: '#ffffff',
        src: '',
        readGlobalHeader: true,
        disable_header: false,
        menuIcon: true,
        searchIcon: true,
        cartIcon: true,
        sidemenuIcon: true,
        sticky_header: 'no',
        search_opened: 'no',
        backButton: 'yes'
      },
      style: {
        colorSource: 'globalColors',
        color: '#000000',
        header_shadow: 'withHeaderShadow',
        icons_bg: 'squareIconBG',
        menu_icon: 'menu',
        search_icon: 'search',
        cart_icon: 'shopping_basket',
        side_menu: 'more_vert',
        font_family: 'Open Sans, sans-serif',
        font_size: '16px',
        font_weight: '400',
        line_height: '32px'
      }
    }

    const mergedData = {
      ...defaultHeader,
      ...templateHeaderData,
      data: {
        ...defaultHeader.data,
        ...(templateHeaderData.data || {})
      }
    }

    if (!mergedData.data.headerTemplate) {
      mergedData.data.headerTemplate = 'template2'
    }

    mergedData.style = {
      ...this.getTemplateStyle(mergedData.data.headerTemplate),
      ...(templateHeaderData.style || {})
    }

    return mergedData
  }

  /**
   * Open "How it works" dialog
   */
  howItWorksDialog = () => {
    const dialogRef = this.dialog.open(WorksDialogComponent, {
      width: '700px',
      maxWidth: '80%',
      data: { name: 'website-templates' }
    })

    dialogRef.afterClosed().subscribe((_result) => {
      console.log('The dialog was closed')
    })
  }
}
